import { CircleHelp, LogOut, Menu, Monitor, Moon, Play, Search, Sun, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { CurrentUserDto } from '@obralink/shared';
import {
  Avatar,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui';
import { useTheme } from '@/hooks/use-theme';
import { useAuth } from '@/app/auth-provider';
import { Campana } from '@/features/notifications/campana';

export function Topbar({
  user,
  onOpenMobileNav,
  onOpenSearch,
  onStartTutorial,
}: {
  user: CurrentUserDto;
  onOpenMobileNav: () => void;
  onOpenSearch: () => void;
  onStartTutorial: () => void;
}) {
  const { theme, setTheme } = useTheme();
  const { logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="flex h-14 w-full shrink-0 items-center gap-2 overflow-hidden border-b border-border bg-surface px-3 md:px-4">
      <Button
        variant="ghost"
        size="icon-sm"
        className="md:hidden"
        data-tour="menu-movil"
        onClick={onOpenMobileNav}
        aria-label="Abrir menú"
      >
        <Menu />
      </Button>

      {/* Buscador global: siempre accesible, y con atajo, porque es la función
          más usada de todo el producto (plan.md §24.1). */}
      <button
        type="button"
        onClick={onOpenSearch}
        data-tour="buscador"
        className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-border-strong bg-surface-2 px-3 text-sm text-fg-subtle transition-colors hover:border-fg-subtle md:max-w-md"
      >
        <Search className="size-4 shrink-0" />
        <span className="truncate">Buscar obras, archivos, personas…</span>
        <kbd className="ml-auto hidden shrink-0 rounded border border-border-strong bg-surface px-1.5 py-0.5 font-mono text-[10px] text-fg-muted md:inline">
          ⌘K
        </kbd>
      </button>

      <div className="ml-auto flex shrink-0 items-center gap-1">
        <div data-tour="avisos" className="flex">
          <Campana />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="ml-1 rounded-full transition-opacity hover:opacity-80"
              data-tour="usuario"
              aria-label="Menú de usuario"
            >
              <Avatar name={user.fullName} size="sm" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <div className="px-2 py-1.5">
              <p className="truncate text-sm font-medium text-fg">{user.fullName}</p>
              <p className="truncate text-xs text-fg-muted">{user.email}</p>
              <Badge tone="brand" className="mt-1.5">
                {user.roleName}
              </Badge>
            </div>
            <DropdownMenuSeparator />

            <DropdownMenuItem onSelect={() => navigate('/perfil')}>
              <User />
              Mi perfil
            </DropdownMenuItem>

            <DropdownMenuItem onSelect={() => navigate('/ayuda')}>
              <CircleHelp />
              Centro de ayuda
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onStartTutorial}>
              <Play />
              Ver el tutorial
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuLabel>Tema</DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => setTheme('light')}>
              <Sun />
              Claro {theme === 'light' && '·'}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setTheme('dark')}>
              <Moon />
              Oscuro {theme === 'dark' && '·'}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setTheme('system')}>
              <Monitor />
              Automático {theme === 'system' && '·'}
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuItem
              danger
              onSelect={() => {
                void logout().then(() => navigate('/acceso', { replace: true }));
              }}
            >
              <LogOut />
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
